/**
 * Classify API failures for UX: outages → banner (no red toast),
 * user-action failures → toast / inline message.
 */
import { friendlyApiError } from './friendlyApiError';
import { isApiOutageError } from '../services/apiAvailability';

export { isApiOutageError };

export function classifyApiError(error) {
  if (!error) {
    return {
      kind: 'unknown',
      severity: 'error',
      message: 'Something went wrong. Please try again.',
      messageKey: 'errors.generic',
    };
  }

  if (error.cancelled || error.code === 'ERR_CANCELED' || error.name === 'CanceledError') {
    return { kind: 'cancelled', severity: 'silent', message: '', messageKey: null };
  }

  const status = error.response?.status;

  if (isApiOutageError(error)) {
    return {
      kind: 'outage',
      severity: 'banner',
      message:
        "We're temporarily unable to reach Coursolo. Please try again in a moment.",
      messageKey: 'common.connectivity.apiDown',
    };
  }

  if (status === 401) {
    return {
      kind: 'auth',
      severity: 'silent',
      message: 'Please sign in again.',
      messageKey: 'common.sessionExpired',
    };
  }

  if (status === 429) {
    return {
      kind: 'rateLimit',
      severity: 'info',
      message: friendlyApiError(error, "You're going a bit fast. Wait a few seconds and try again."),
      messageKey: 'errors.rateLimit',
    };
  }

  if (status === 403) {
    return {
      kind: 'forbidden',
      severity: 'error',
      message: friendlyApiError(error, 'You do not have permission for this action.'),
      messageKey: null,
    };
  }

  if (status >= 400 && status < 500) {
    return {
      kind: 'client',
      severity: 'error',
      message: friendlyApiError(error, error.userMessage || 'Please check your input and try again.'),
      messageKey: null,
    };
  }

  return {
    kind: 'unknown',
    severity: 'error',
    message: error.userMessage || friendlyApiError(error),
    messageKey: 'errors.generic',
  };
}

export default classifyApiError;
